// Overpass API — free OpenStreetMap query engine, no key required.
const ENDPOINTS = [
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass-api.de/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
];

// tourism=viewpoint is the gold standard for bikers — these nodes are explicitly
// tagged by OSM contributors as scenic and are almost always on winding elevated roads.
// All terrain types include viewpoints as a primary source; secondary queries add
// terrain-specific flavour.
const QUERIES = {
  mountain: (lat, lng, r) => `
[out:json][timeout:8];
(
  node["tourism"="viewpoint"]["name"](around:${r},${lat},${lng});
  node["natural"="peak"]["name"]["ele"](around:${r},${lat},${lng});
  node["mountain_pass"="yes"]["name"](around:${r},${lat},${lng});
  node["natural"="saddle"]["name"](around:${r},${lat},${lng});
);
out;`,

  coastal: (lat, lng, r) => `
[out:json][timeout:8];
(
  node["tourism"="viewpoint"]["name"](around:${r},${lat},${lng});
  node["natural"="cape"]["name"](around:${r},${lat},${lng});
  node["natural"="headland"]["name"](around:${r},${lat},${lng});
  node["natural"="cliff"]["name"](around:${r},${lat},${lng});
  node["natural"="beach"]["name"](around:${r},${lat},${lng});
);
out;`,

  nature: (lat, lng, r) => `
[out:json][timeout:8];
(
  node["tourism"="viewpoint"]["name"](around:${r},${lat},${lng});
  node["natural"="waterfall"]["name"](around:${r},${lat},${lng});
  node["leisure"="nature_reserve"]["name"](around:${r},${lat},${lng});
  way["leisure"="nature_reserve"]["name"](around:${r},${lat},${lng});
  way["boundary"="national_park"]["name"](around:${r},${lat},${lng});
);
out center;`,

  // Villages and hamlets only — "town" can mean a large urban centre.
  // Cafes added as genuine biker-stop destinations.
  towns: (lat, lng, r) => `
[out:json][timeout:8];
(
  node["place"="village"]["name"](around:${r},${lat},${lng});
  node["place"="hamlet"]["name"](around:${r},${lat},${lng});
  node["tourism"="viewpoint"]["name"](around:${r},${lat},${lng});
  node["amenity"="cafe"]["name"](around:${r},${lat},${lng});
);
out;`,

  scenic: (lat, lng, r) => `
[out:json][timeout:8];
(
  node["tourism"="viewpoint"]["name"](around:${r},${lat},${lng});
  node["natural"="peak"]["name"]["ele"](around:${r},${lat},${lng});
  node["natural"="waterfall"]["name"](around:${r},${lat},${lng});
  node["historic"="lighthouse"]["name"](around:${r},${lat},${lng});
  node["historic"="castle"]["name"](around:${r},${lat},${lng});
);
out;`,

  // Best-of-all for "anywhere" — combines the highest-value POI types across
  // all terrains so ORS is routed through genuinely interesting places.
  anywhere: (lat, lng, r) => `
[out:json][timeout:15];
(
  node["tourism"="viewpoint"]["name"](around:${r},${lat},${lng});
  node["natural"="peak"]["name"]["ele"](around:${r},${lat},${lng});
  node["place"="village"]["name"](around:${r},${lat},${lng});
  node["place"="town"]["name"](around:${r},${lat},${lng});
  node["natural"="waterfall"]["name"](around:${r},${lat},${lng});
  node["mountain_pass"="yes"]["name"](around:${r},${lat},${lng});
  node["natural"="cape"]["name"](around:${r},${lat},${lng});
  node["historic"="castle"]["name"](around:${r},${lat},${lng});
);
out;`,

  // Absolute fallback — any named settlement, used when all other terrain types fail.
  settlements: (lat, lng, r) => `
[out:json][timeout:15];
(
  node["place"~"city|town|village|hamlet|suburb"]["name"](around:${r},${lat},${lng});
  node["tourism"~"attraction|viewpoint"]["name"](around:${r},${lat},${lng});
  node["amenity"="fuel"]["name"](around:${r},${lat},${lng});
);
out;`,
};

import { withTimeout } from './fetchUtils';

async function postQuery(url, query, signal) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `data=${encodeURIComponent(query)}`,
    signal,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * Find named destinations (towns/villages/viewpoints/peaks/attractions) near a
 * set of target points — one Overpass `around` union query, node-only, so the
 * payload stays tiny and fast. Returns [{lat,lng,name,tags}], [] on failure.
 */
export async function fetchPlacesNearPoints(points, radiusM, signal) {
  if (!points.length) return [];
  const clauses = points.map(p => {
    const ll = `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
    return (
      `node["place"~"^(city|town|village|hamlet)$"]["name"](around:${radiusM},${ll});` +
      `node["tourism"~"^(viewpoint|attraction)$"]["name"](around:${radiusM},${ll});` +
      `node["natural"="peak"]["name"](around:${radiusM},${ll});`
    );
  }).join('');
  const query = `[out:json][timeout:12];(${clauses});out;`;

  let data;
  try {
    data = await Promise.any(
      ENDPOINTS.map(url => postQuery(url, query, withTimeout(12000, signal)))
    );
  } catch {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return [];
  }

  return (data.elements || [])
    .map(el => ({
      lat: el.lat,
      lng: el.lon,
      name: el.tags?.name || el.tags?.['name:en'] || '',
      tags: el.tags || {},
    }))
    .filter(p => p.lat != null && p.lng != null && p.name);
}

/**
 * Fetch POIs of a given terrain type within radiusKm of lat/lng.
 * Both Overpass mirrors are raced in parallel with a hard 9s cap —
 * whichever answers first wins. Returns [] on total failure.
 */
export async function fetchTerrainPoints(lat, lng, radiusKm, terrainType, signal) {
  const build = QUERIES[terrainType];
  if (!build) return [];

  const radiusM = Math.round(radiusKm * 1000);
  const query = build(lat, lng, radiusM);

  let data;
  try {
    data = await Promise.any(
      ENDPOINTS.map(url => postQuery(url, query, withTimeout(15000, signal)))
    );
  } catch {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return [];
  }

  return (data.elements || [])
    .map(el => ({
      lat: el.lat ?? el.center?.lat,
      lng: el.lon ?? el.center?.lon,
      name: el.tags?.name || el.tags?.['name:en'] || '',
      tags: el.tags || {},
    }))
    .filter(p => p.lat != null && p.lng != null && p.name);
}
